import type { MatchState } from '../sim/state';
import type { SimConfig } from '../config/simConfig';
import { DT } from '../config/simConfig';
import { PITCH } from '../config/pitchConfig';
import { rngInt } from '../math/rng';

// Referee movement AI (§7.5) — diagonal-follow behind play, min distance from the ball, capped speed; gaze
// tracks the ball except during a periodic look-away scan (period drawn from state.rng — the readable,
// exploitable window). Stays out of the penalty boxes except for incidents (P13). The scan draw is the LAST
// state.rng draw of refereeStep (§7 order), taken here.

/** Centroid of the off-ball outfield cluster (the densest look-away target). Deterministic. */
function offBallCluster(state: MatchState): { x: number; y: number } {
  let sx = 0;
  let sy = 0;
  let n = 0;
  for (const p of state.players) {
    if (p.sentOff) continue;
    if (Math.hypot(p.pos.x - state.ball.pos.x, p.pos.y - state.ball.pos.y) < 5) continue; // skip on-ball players
    sx += p.pos.x;
    sy += p.pos.y;
    n += 1;
  }
  return n > 0 ? { x: sx / n, y: sy / n } : { x: state.ball.pos.x, y: state.ball.pos.y };
}

export function moveReferee(state: MatchState, cfg: SimConfig, incident = false): void {
  const ref = state.referee;
  const R = cfg.referee;
  const pitch = PITCH[state.mode.teamSize];
  const ball = state.ball.pos;

  // diagonal-follow target behind play, held at least REF_MIN_DIST from the ball
  let tx = ball.x - R.BEHIND_PLAY_OFFSET;
  let ty = ball.y + R.BEHIND_PLAY_OFFSET;
  const dbx = tx - ball.x;
  const dby = ty - ball.y;
  const db = Math.hypot(dbx, dby) || 1;
  if (db < R.REF_MIN_DIST) {
    tx = ball.x + (dbx / db) * R.REF_MIN_DIST;
    ty = ball.y + (dby / db) * R.REF_MIN_DIST;
  }
  // stay out of the penalty boxes unless there's an incident to attend (P13)
  if (!incident && Math.abs(ty) <= pitch.penaltyBoxHalfWidth) {
    const boxEdge = pitch.length / 2 - pitch.penaltyBoxDepth;
    if (tx > boxEdge) tx = boxEdge;
    else if (tx < -boxEdge) tx = -boxEdge;
  }
  ref.aiTargetPos = { x: tx, y: ty };

  // move toward the target, capped at REF_MAX_SPEED
  const mdx = tx - ref.pos.x;
  const mdy = ty - ref.pos.y;
  const md = Math.hypot(mdx, mdy);
  if (md > 1e-6) {
    const s = Math.min(md, R.REF_MAX_SPEED * DT);
    ref.pos.x += (mdx / md) * s;
    ref.pos.y += (mdy / md) * s;
    ref.vel = { x: (mdx / md) * (s / DT), y: (mdy / md) * (s / DT) };
  } else {
    ref.vel = { x: 0, y: 0 };
  }

  // scan timer (§7.5): one state.rng draw when the period expires — the LAST draw of refereeStep
  ref.scanTicksLeft -= 1;
  const scanning = ref.scanTicksLeft > 0 && ref.scanTicksLeft <= R.SCAN_DUR;
  if (ref.scanTicksLeft <= 0) {
    ref.scanTicksLeft = R.SCAN_PERIOD + (rngInt(state, 2 * R.SCAN_JITTER + 1) - R.SCAN_JITTER);
  }
  const gaze = scanning ? offBallCluster(state) : ball;
  ref.viewDir = Math.atan2(gaze.y - ref.pos.y, gaze.x - ref.pos.x);
}
